/**
 * People (`/admin/people`): the institution's register of every person, as
 * one Bento page (P03 to P07). The register is a List whose find and filters
 * are its own tools and whose selection is its own (R6); Send fresh setup
 * emails and archive/restore are scoped bulk Tasks over that selection; Add person is a Task in
 * place; the managed Google accounts are their own Fact. A person's record is
 * a visit (`?visit=person:<id>`), drawn in the register's place (O10).
 *
 * The filters and the page live in the address, so a link, a reload and Back
 * agree on what is read. The selection lasts across filtering and paging until
 * a send confirms or the person clears it.
 */
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { Bento } from "@/components/ui/box";
import type { Box, ListBox, ListFacet } from "@/components/ui/box.schema";
import { PeopleRegisterTable } from "@/components/people/register/PeopleRegisterTable";
import { choicesRead, useAddTeacher, useBulkSend, useBulkVisibility, useFilterChoices, useManagedAccounts, useRegister, useSelection, useSelectionReads } from "@/components/people/register/register-boxes";
import { registerCopy as copy } from "@/components/people/register/register-copy";
import type { PeopleRegisterResponse, PeopleRegisterRow } from "@/lib/api/people";
import { VIEWS, ACCESS, RELATIONSHIPS, ATTENTION, STATUSES, TEACHING, peopleFiltersFromSearchParams } from "./people-filters";

const ANY = "";

export function PeoplePage() {
  const [params, setParams] = useSearchParams();
  // The address owns the register reading. The kit keeps immediate typing while
  // this debounced echo restores the same find after Return, reload and links.
  const q = params.get("q") ?? "";
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const filters = peopleFiltersFromSearchParams(params, q);

  const set = (changes: Record<string, string | null>) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [name, value] of Object.entries(changes)) {
        next.delete(name);
        if (value) next.set(name, value);
      }
      if (!("page" in changes)) next.delete("page");
      return next;
    }, { replace: true });
  const onQuery = (query: string) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      set({ q: query || null, page: null });
    }, 300);
  };

  const studentScope = Boolean(filters.student_view) || filters.relationship?.[0] === "student";
  const facultyScope = filters.relationship?.[0] === "faculty";
  const choices = useFilterChoices({ cohorts: studentScope });

  const viewValue = filters.student_view ?? filters.view ?? "operational";
  const facets: ListFacet[] = [
    {
      label: copy.view,
      value: viewValue,
      options: VIEWS.map(({ value, label }) => ({ value, label })),
      onChange: (value) => {
        const chosen = VIEWS.find((view) => view.value === value) ?? VIEWS[0];
        set({ view: chosen.view === "operational" ? null : chosen.view, student_view: chosen.student ?? null });
      },
    },
    {
      label: copy.access,
      value: filters.access?.[0] ?? ANY,
      options: [{ value: ANY, label: copy.accessAll }, ...ACCESS.map(([value, label]) => ({ value, label }))],
      onChange: (value) => set({ access: value || null }),
    },
    {
      label: copy.relationship,
      value: filters.relationship?.[0] ?? ANY,
      options: [{ value: ANY, label: copy.relationshipAll }, ...RELATIONSHIPS.map(([value, label]) => ({ value, label }))],
      onChange: (value) => set({ relationship: value || null, ...(value !== "student" ? { cohort_id: null, student_status: null } : {}), ...(value !== "faculty" ? { teaching: null } : {}) }),
    },
    {
      label: copy.program,
      value: filters.program_id ?? ANY,
      options: [
        { value: ANY, label: copy.programAll },
        ...(choices.programs.data ?? []).map((program) => ({ value: program.id, label: program.name })),
        ...(filters.program_id && !choices.programs.data?.some((program) => program.id === filters.program_id) ? [{ value: filters.program_id, label: copy.program }] : []),
      ],
      onChange: (value) => set({ program_id: value || null }),
      read: choicesRead(choices.programs),
    },
    ...(studentScope
      ? [
          {
            label: copy.cohort,
            value: filters.cohort_id ?? ANY,
            options: [
              { value: ANY, label: copy.cohortAll },
              ...(choices.cohorts.data ?? []).map((cohort) => ({ value: cohort.id, label: cohort.label })),
              ...(filters.cohort_id && !choices.cohorts.data?.some((cohort) => cohort.id === filters.cohort_id) ? [{ value: filters.cohort_id, label: copy.cohort }] : []),
            ],
            onChange: (value: string) => set({ cohort_id: value || null }),
            read: choicesRead(choices.cohorts),
          },
          {
            label: copy.status,
            value: filters.student_status ?? ANY,
            options: [{ value: ANY, label: copy.statusAll }, ...STATUSES.map(([value, label]) => ({ value, label }))],
            onChange: (value: string) => set({ student_status: value || null }),
          },
        ]
      : []),
    ...(facultyScope
      ? [{
          label: copy.teaching,
          value: filters.teaching ?? ANY,
          options: [{ value: ANY, label: copy.teachingAll }, ...TEACHING.map(([value, label]) => ({ value, label }))],
          onChange: (value: string) => set({ teaching: value || null }),
        }]
      : []),
    {
      label: copy.attention,
      value: filters.attention?.[0] ?? ANY,
      options: [{ value: ANY, label: copy.attentionAll }, ...ATTENTION.map(([value, label]) => ({ value, label }))],
      onChange: (value) => set({ attention: value || null }),
    },
    {
      label: copy.term,
      value: filters.semester_id ?? ANY,
      options: [
        { value: ANY, label: copy.termAll },
        ...(choices.semesters.data ?? []).map((semester) => ({ value: semester.id, label: semester.name })),
        ...(filters.semester_id && !choices.semesters.data?.some((semester) => semester.id === filters.semester_id) ? [{ value: filters.semester_id, label: copy.term }] : []),
      ],
      onChange: (value) => set({ semester_id: value || null }),
      read: choicesRead(choices.semesters),
    },
  ];

  const { selection, setSelection, toggle } = useSelection();
  const { register, query, read } = useRegister({ filters, facets, onQuery }, selection, toggle);
  const response = query.data;
  const rows = response?.rows ?? [];
  useSelectionReads(rows, setSelection);

  // The page of the register is a choice of the List's read, never an act (R6).
  const pageSize = filters.page_size ?? 25;
  const total = response?.total ?? 0;
  // An address past the last page reads the last page, so the List never draws zero of its people as a page.
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const pastEnd = Boolean(response && !query.isPlaceholderData && total > 0 && rows.length === 0 && (filters.page ?? 1) > lastPage);
  useEffect(() => {
    if (pastEnd) set({ page: lastPage === 1 ? null : String(lastPage) });
    // `set` is made each render; the address change is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastEnd, lastPage]);
  const bulk = useBulkSend(register, rows, selection, setSelection);
  const visibility = useBulkVisibility(selection, setSelection, rows);
  const add = useAddTeacher(read, facultyScope);
  const managed = useManagedAccounts();
  const list = register as ListBox<PeopleRegisterResponse, PeopleRegisterRow>;
  // A successful empty server page still supplies the native table controls
  // and hidden selection. Failure/staleness never becomes an invented zero.
  const tableRegister = {
    ...list,
    working: true,
    renderTools: () => null,
    read: list.read.status === "empty" && response
      ? { status: "ready" as const, value: response, readAt: new Date(query.dataUpdatedAt).toISOString() }
      : list.read,
    find: list.find ? { ...list.find, query: q } : undefined,
    renderRows: (value: PeopleRegisterResponse) => <PeopleRegisterTable
      find={list.find ? { ...list.find, query: q } : undefined}
      response={value} selection={selection} setSelection={setSelection}
      onPage={page => set({ page: page === 1 ? null : String(page) })}
      onClearFilters={() => set(Object.fromEntries(["q", "view", "student_view", "access", "relationship", "attention", "program_id", "cohort_id", "student_status", "teaching", "semester_id", "page"].map(key => [key, null])))}
      stateOf={row => list.row.state?.(row) ?? null}
      actOf={row => list.row.act?.(row)}
      emptySentence={list.read.status === "empty" ? list.read.sentence : copy.noMatch}
    />,
  };
  const showVisibility = selection.size > 0 || visibility.kind === "task" && (visibility.result.state !== "none" || visibility.draft);
  const boxes: Box[] = [add, ...(showVisibility ? [visibility] : []), ...(bulk ? [bulk] : []), tableRegister as unknown as Box, ...(managed ? [managed] : [])];

  return (
    <div className="app-page">
      <AcademicPageHeader eyebrow={copy.eyebrow} title={copy.title} description={copy.description} />
      <Bento id="people" journey="people" boxes={boxes} />
    </div>
  );
}
